import { BaseType, select as d3Select } from "d3";
import { produce } from "immer";
import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";

import { Cartesian, Stem } from "@examspark/types";

import { calculateScales } from "./d3Utils";
import { useGraphDecorator } from "./decorators/drawGraph";
import { calculateQuadrants } from "./decorators/drawInequalityQuadrants";
import { useListenPointsDecorator } from "./decorators/drawListenPoints";
import { useMarginLabels } from "./decorators/drawMarginLabels";
import { useShapesDecorator } from "./decorators/drawShapes";

export type WithIndex<T> = T & { index: number };

const Styled = styled.div`
  user-select: none;
  display: flex;
  justify-content: center;
  svg {
    display: block;
  }
`;

const Wrapper = styled.div`
  background-color: #fff;
  border: solid 1px #ddd;
`;

export interface CartesianInequalityProps extends Cartesian.Graph {
  mode?: string;
  selectedQuadrant?: number;
  onChange?: (scoring: {
    shapes?: Stem.Scoring["shapes"];
    inequality?: Stem.Scoring["inequality"];
  }) => void;
}

const CartesianInequality: React.FC<
  React.PropsWithChildren<CartesianInequalityProps>
> = (props) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const extra_margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };
  if (props.marginLabelTop || props.y.label) {
    extra_margin.top = 20;
  }

  // we need to make room for long x labels
  if (props.marginLabelRight || props.x.label) {
    extra_margin.right = Math.max(20, 5 * (props.x.label?.length ?? 0));
  }
  if (props.marginLabelBottom) {
    extra_margin.bottom = 20;
  }
  if (props.marginLabelLeft) {
    extra_margin.left = 20;
  }

  // const margin = { top: 0, right: 0, bottom: 0, left: 0 };
  const margin = {
    top: 20 + extra_margin.top,
    right: 20 + extra_margin.right,
    bottom: 20 + extra_margin.bottom,
    left: 30 + extra_margin.left,
  };
  const width = props.width - margin.left - margin.right;
  const height = props.height - margin.top - margin.bottom;

  const { x, y } = calculateScales({ width, height, x: props.x, y: props.y });

  const moveHandler = (
    shapeIndex: number,
    coordIndex: number,
    coord: Cartesian.Coordinate,
  ) => {
    if (props.mode !== undefined) return;

    const newShapes = produce(props.shapes, (draft) => {
      const shape = draft[shapeIndex];
      if ("coords" in shape) {
        shape.coords[coordIndex] = coord;
      } else {
        shape.coord = coord;
      }

      return draft;
    });
    props.onChange?.({
      shapes: newShapes,
    });
  };

  const decorateGraph = useGraphDecorator<SVGSVGElement>({
    x,
    y,
    margin,
  });

  const decorateShapes = useShapesDecorator<BaseType | SVGGElement>({
    x,
    y,
    onErase: (shapeIndex) => {
      if (props.mode !== "erase") return;

      const newShapes = [...props.shapes];
      newShapes.splice(shapeIndex, 1);

      props.onChange?.({
        shapes: newShapes,
      });
    },
    // do not attach a move handler unless we need it. This helps
    // the click handler for erase fire more reliably
    onMove: props.mode === undefined ? moveHandler : undefined,
  });

  const decorateQuadrants = useShapesDecorator<BaseType | SVGGElement>({
    x,
    y,
  });

  const decorateListenPoints = useListenPointsDecorator({
    x,
    y,
    mode: props.mode,
    shapes: props.shapes,
    onChange: (shapes: Stem.Scoring["shapes"]) => props.onChange?.({ shapes }),
  });

  const decorateMarginLabels = useMarginLabels({
    width,
    height,
    props,
    margin,
  });

  // inequalities only work on graphs with 1 or 2 line shapes
  const [line_1, line_2] = props.shapes.filter(
    (shape): shape is Cartesian.Shape.Line | Cartesian.Shape.DashedLine =>
      shape.type === "line" || shape.type === "dashed-line",
  );
  const extraShapes = calculateQuadrants({
    line_1,
    line_2,
    x,
    y,
  });

  useLayoutEffect(() => {
    if (!svgRef.current) return;

    const canvas = d3Select(svgRef.current);

    const graph = canvas.call(decorateGraph).select(".graph");

    graph.call(decorateMarginLabels);

    const quadrants = graph
      .selectAll("g.extra-shapes")
      .data([null])
      .join("g")
      .attr("class", "extra-shapes");

    const quadrant = quadrants
      .selectAll("g.extra-shape")
      .data<WithIndex<Cartesian.Shape>>(
        (extraShapes ?? []).map((shape, index) => ({ ...shape, index })),
      )
      .join("g")
      .attr("class", "extra-shape")
      .call(decorateQuadrants);

    // const quadrant_shape =
    quadrant
      .selectAll("polygon")
      .data((d) => [d])
      .on("mouseover", function () {
        const d = d3Select(this).datum() as WithIndex<Cartesian.Shape>;
        if (d.index === props.selectedQuadrant) return;

        d3Select(this).attr("fill-opacity", 0.2);
      })
      .on("mouseout", function () {
        const d = d3Select(this).datum() as WithIndex<Cartesian.Shape>;
        if (d.index === props.selectedQuadrant) return;

        d3Select(this).attr("fill-opacity", 0);
      })
      .on("click", function () {
        if (props.mode === "answer") {
          const d = d3Select(this).datum() as WithIndex<Cartesian.Shape>;

          props.onChange?.({
            inequality: {
              quadrant: d.index,
            },
          });
        }
      })
      .attr("fill", (d, i) => "#1fa4e1")
      .attr("fill-opacity", (d) =>
        props.selectedQuadrant === d.index ? 0.4 : 0,
      )
      .attr("stroke-width", 0);

    // const quadrant_labels =
    // quadrant
    //   .selectAll("text.shape-label")
    //   .data((d) => [d])
    //   .join("text")
    //   .attr("class", "shape-label")
    //   .attr("text-anchor", "middle")
    //   .attr("dominant-baseline", "middle")
    //   .attr("font-size", 36)
    //   .attr("fill", "#000")
    //   .attr("x", (d) =>
    //     x.scale(
    //       "coord" in d
    //         ? d.coord.x
    //         : d.coords.reduce((acc, coord) => acc + coord.x, 0) /
    //             d.coords.length
    //     )
    //   )
    //   .attr("y", (d) =>
    //     y.scale(
    //       "coord" in d
    //         ? d.coord.y
    //         : d.coords.reduce((acc, coord) => acc + coord.y, 0) /
    //             d.coords.length
    //     )
    //   )
    //   .text((d, i) => d.index + 1);

    quadrant
      .selectAll(".shape-coords, .shape-coord-center")
      .style("opacity", 0);

    // prettier-ignore
    graph
      .selectAll("g.shapes")
        .data([null])
        .join("g")
          .attr("class", "shapes")
        .selectAll("g.shape")
          .data(props.shapes.map((shape, index) => ({ ...shape, index })))
          .join("g")
            .attr("class", "shape")
          .call(decorateShapes);

    graph.call(decorateListenPoints);
  });

  return (
    <Styled>
      <Wrapper>
        <svg
          ref={svgRef}
          width={props.width}
          height={props.height}
          style={{ shapeRendering: "geometricPrecision" }}
        >
          <defs>
            <marker
              id="arrow"
              markerWidth="13"
              markerHeight="13"
              refX="1.5"
              refY="4.5"
              style={{ fill: "#1fa4e1" }}
              orient="auto-start-reverse"
            >
              <path d="M 1.5 1.5 L 1.5 8.25 L 7.5 4.5 L 1.5 1.5" />
            </marker>
            <marker
              id="arrow-with-tail"
              markerWidth="13"
              markerHeight="13"
              refX="3"
              refY="4.5"
              style={{ fill: "black" }}
              orient="auto-start-reverse"
            >
              <path d="M 6 1.5 L 6 4 L 3 4 L 3 5 L 6 5 L 6 7.5 L 12 4.5 L 6 1.5" />
            </marker>
          </defs>
        </svg>
      </Wrapper>
    </Styled>
  );
};

export default CartesianInequality;
